import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { debounce } from 'lodash';
import { Modal, ModalBody, ModalHeader, Label } from 'reactstrap';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import {
  DEPARTMENTS,
  DEPARTMENT_CS,
  DEPARTMENT_FN,
  DEPARTMENT_MKT,
  DEPARTMENT_PS,
  ROLE_AE,
  ROLE_AM,
  ROLE_CSE,
  ROLE_CSL,
  ROLE_CSM,
  ROLE_FL,
  ROLE_ML,
  ROLE_MM,
  ROLE_PSE,
  ROLE_PSL,
  ROLE_PSM,
  getIntranetFeatures,
} from 'constants/department';
import Button from 'components/App/Button';
import AppAvatar from 'components/App/Avatar';
import AppBadge from 'components/App/AppBadge';
import AppSelectValidation from 'components/App/SelectValidation';

import styles from './FeaturePermission.module.scss';

const MARKET_PAGE_LIMIT = 10;
const INITIAL_MARKET_VALUES = { markets: [] };

function FeaturePermission(props) {
  const {
    isOpen,
    onToggleModal,
    onNext,
    accountInfo,
    marketList,
    marketTotal,
    isLoadingMarketList,
    actionGetMarketList,
  } = props;

  const isPsmRole = accountInfo.department === ROLE_PSM;

  const departmentCurrent = React.useMemo(() => DEPARTMENTS.reduce((found, dept) => {
    if (found) return found;
    if (dept.value === accountInfo.department) return dept;

    const roleFound = dept.roles?.find((role) => role.value === accountInfo.department);
    return roleFound || found;
  }, undefined), [accountInfo.department]);

  const [marketPage, setMarketPage] = React.useState(1);
  const [marketSearchQuery, setMarketSearchQuery] = React.useState('');
  const hasInitializedRef = React.useRef(false);

  const departmentBadgeColor = React.useMemo(() => {
    switch (departmentCurrent?.value) {
      case DEPARTMENT_PS:
      case ROLE_PSL:
      case ROLE_PSM:
      case ROLE_PSE:
        return 'info';
      case DEPARTMENT_CS:
      case ROLE_CSL:
      case ROLE_CSM:
      case ROLE_CSE:
        return 'warning';
      case DEPARTMENT_FN:
      case ROLE_FL:
      case ROLE_AM:
      case ROLE_AE:
        return 'success';
      case DEPARTMENT_MKT:
      case ROLE_ML:
      case ROLE_MM:
        return 'caution';
      default:
        return 'neutral';
    }
  }, [departmentCurrent]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        markets: isPsmRole
          ? Yup.array().min(1, 'Vui lòng chọn ít nhất 1 chỗ nghỉ quản lý')
          : Yup.array(),
      }),
    [isPsmRole],
  );

  const validation = useFormik({
    initialValues: INITIAL_MARKET_VALUES,
    validationSchema,
    onSubmit: (values) => {
      if (!isPsmRole) {
        onNext({});
        return;
      }

      const selectedMarkets = values.markets || [];

      onNext({
        markets: selectedMarkets.map((item) => item.value),
      });
    },
  });

  const hasMoreMarkets = marketList.length < marketTotal;

  const buildMarketListPayload = React.useCallback(({ page, append, q }) => {
    const payload = {
      page,
      limit: MARKET_PAGE_LIMIT,
      append,
      isWithoutPsm: true,
    };

    if (q) {
      payload.q = q;
    }

    return payload;
  }, []);

  const marketOptions = React.useMemo(
    () => marketList.map((item) => ({ label: item.name, value: item._id })),
    [marketList],
  );

  const onMarketChangeMulti = React.useCallback(
    (selectedOptions) => {
      validation.setFieldValue('markets', selectedOptions || [], true);
    },
    [validation],
  );

  const debouncedSearchMarkets = React.useMemo(
    () =>
      debounce((searchValue) => {
        setMarketSearchQuery(searchValue);
        setMarketPage(1);
        actionGetMarketList(
          buildMarketListPayload({
            page: 1,
            append: false,
            q: searchValue,
          }),
        );
      }, 400),
    [actionGetMarketList, buildMarketListPayload],
  );

  const onMarketInputChange = React.useCallback(
    (inputValue, { action }) => {
      if (action === 'input-change') {
        debouncedSearchMarkets(inputValue);
      }
    },
    [debouncedSearchMarkets],
  );

  const resetMarketList = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    actionGetMarketList(
      buildMarketListPayload({ page: 1, append: false, q: '' }),
    );
  }, [actionGetMarketList, buildMarketListPayload, debouncedSearchMarkets]);

  const onMarketMenuOpen = React.useCallback(() => {
    resetMarketList();
  }, [resetMarketList]);

  const onMarketBlur = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
  }, [debouncedSearchMarkets]);

  const onLoadMoreMarkets = React.useCallback(() => {
    if (isLoadingMarketList || !hasMoreMarkets) {
      return;
    }

    const nextPage = marketPage + 1;
    setMarketPage(nextPage);
    actionGetMarketList(
      buildMarketListPayload({
        page: nextPage,
        append: true,
        q: marketSearchQuery,
      }),
    );
  }, [
    actionGetMarketList,
    buildMarketListPayload,
    hasMoreMarkets,
    isLoadingMarketList,
    marketPage,
    marketSearchQuery,
  ]);

  const intranetFeatures = React.useMemo(() =>
    getIntranetFeatures(accountInfo?.department), [accountInfo?.department]);

  React.useEffect(
    () => () => debouncedSearchMarkets.cancel(),
    [debouncedSearchMarkets],
  );

  React.useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      validation.resetForm({ values: INITIAL_MARKET_VALUES });
      setMarketPage(1);
      setMarketSearchQuery('');
      debouncedSearchMarkets.cancel();
    }
  }, [isOpen, debouncedSearchMarkets, validation]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Chỉ khởi tạo một lần khi modal mở, tránh reset selections khi parent re-render
    // do Redux store update (accountInfo.markets có reference mới dù giá trị không đổi)
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    let marketIds = [];

    if (accountInfo.markets?.length) {
      marketIds = accountInfo.markets;
    } else if (accountInfo.market) {
      marketIds = [accountInfo.market];
    }

    const initialMarkets = marketIds
      .map((marketItemOrId) => {
        if (marketItemOrId && typeof marketItemOrId === 'object') {
          return {
            label: marketItemOrId.name?.trim() || '',
            value: marketItemOrId._id || '',
          };
        }

        const marketItem = marketList.find((item) => item._id === marketItemOrId);

        return {
          label: marketItem?.name || '',
          value: marketItemOrId,
        };
      })
      .filter((item) => item.value);

    validation.resetForm({ values: { markets: initialMarkets } });
  }, [isOpen, accountInfo.market, accountInfo.markets]);

  React.useEffect(() => {
    if (!isOpen || !marketList.length || !validation.values.markets?.length) {
      return;
    }

    const enrichedMarkets = validation.values.markets.map((selected) => {
      if (selected.label) {
        return selected;
      }

      const marketItem = marketList.find((item) => item._id === selected.value);

      if (!marketItem) {
        return selected;
      }

      return {
        label: marketItem.name,
        value: marketItem._id,
      };
    });

    const hasLabelUpdates = enrichedMarkets.some(
      (item, index) => item.label !== validation.values.markets[index]?.label,
    );

    if (hasLabelUpdates) {
      validation.setFieldValue('markets', enrichedMarkets, false);
    }
  }, [isOpen, marketList, validation.values.markets, validation]);

  const handleNext = React.useCallback(() => {
    validation.submitForm();
  }, [validation]);

  const CloseButton = React.useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  return (
    <Modal
      fade
      size="lg"
      isOpen={isOpen}
      toggle={onToggleModal}
      className={styles.Modal}
    >
      <ModalHeader
        toggle={onToggleModal}
        className={styles.ModalHeader}
        close={CloseButton}
      />

      <ModalBody className={styles.ModalBody}>
        <Label className="Title">
          Người dùng này có thể truy cập được những chức năng sau:
        </Label>
        <hr className="Divider" />

        <div className="UserCard">
          <div className="UserCard__Profile">
            <AppAvatar name={accountInfo.fullName} />
            <div className="UserCard__Info">
              <p className="UserCard__Name">{accountInfo.fullName}</p>
              <p className="UserCard__Email">{accountInfo.email}</p>
            </div>
          </div>

          <AppBadge
            color={departmentBadgeColor}
            className="UserCard__Department"
          >
            {departmentCurrent?.label}
          </AppBadge>
        </div>

        <div className="Section">
          {isPsmRole && (
            <>
              <p className="Section__Title">Extranet</p>
              <p className="Section__Description">
                Quản lý những extranet của chỗ nghỉ trong thị trường được phân
                phối
              </p>
              <AppSelectValidation
                placeholder="Chọn thị trường quản lý"
                name="markets"
                key="markets"
                options={marketOptions}
                validation={validation}
                value={validation.values.markets}
                isMulti
                isHiddenSearchable
                onChange={() => {}}
                onChangeMulti={onMarketChangeMulti}
                onInputChange={onMarketInputChange}
                onBlur={onMarketBlur}
                onMenuOpen={onMarketMenuOpen}
                onMenuScrollToBottom={onLoadMoreMarkets}
                filterOption={() => true}
                isLoading={isLoadingMarketList}
                isHiddenClearable
              />
            </>
          )}
        </div>

        <div className="Section">
          <p className="Section__Title">Intranet</p>
          <p className="Section__Description">
            Quản lý quyền truy cập vào hệ thống nội bộ
          </p>

          <div className="IntranetTable">
            <div className="IntranetTable__Row">
              <div className="IntranetTable__Cell">Intranet</div>
              <div className="IntranetTable__Cell">
                {intranetFeatures}
              </div>
            </div>
          </div>
        </div>

        <hr className="Divider" />

        <div className="Button__Wrapper">
          <Button
            classes="Button Button__Cancel"
            type="button"
            onClick={onToggleModal}
          >
            Hủy
          </Button>

          <Button
            classes="Button Button__Submit"
            type="button"
            onClick={handleNext}
          >
            Tiếp theo
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

FeaturePermission.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  accountInfo: PropTypes.instanceOf(Object),
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,
  isLoadingMarketList: PropTypes.bool,
  actionGetMarketList: PropTypes.func,
};

FeaturePermission.defaultProps = {
  accountInfo: {},
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
  actionGetMarketList: () => {},
};

export default React.memo(FeaturePermission);
